import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet } from './utils/apiService';
import SelectorTemporalSlider from './SelectorTemporalSlider';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';

// Configuración de la API
const API_BASE = process.env.REACT_APP_API_URL || 'https://municipal-dashboard-api.repasador-alpargata.workers.dev';

const EstadisticasProveedores = () => {
  const [estadisticas, setEstadisticas] = useState(null);
  const [topProveedores, setTopProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rangoPeriodo, setRangoPeriodo] = useState({});

  // Memoizar la key del useEffect para evitar re-renders innecesarios
  const rangoKey = useMemo(() => 
    JSON.stringify(rangoPeriodo), [rangoPeriodo]
  );

  useEffect(() => {
    cargarDatos();
  }, [rangoKey]);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Construir query params para filtros
      const params = new URLSearchParams();
      if (rangoPeriodo.mesInicio) params.append('mesInicio', rangoPeriodo.mesInicio);
      if (rangoPeriodo.anoInicio) params.append('anoInicio', rangoPeriodo.anoInicio);
      if (rangoPeriodo.mesFin) params.append('mesFin', rangoPeriodo.mesFin);
      if (rangoPeriodo.anoFin) params.append('anoFin', rangoPeriodo.anoFin);
      
      const queryString = params.toString();
      
      // Cargar estadísticas generales con filtros
      const estadisticasData = await apiGet(`${API_BASE}/estadisticas${queryString ? '?' + queryString : ''}`);
      setEstadisticas(estadisticasData);

      // Cargar top 30 proveedores con filtros
      const topParams = new URLSearchParams(params);
      topParams.append('limit', '30');
      topParams.append('ordenar', 'monto');
      const topData = await apiGet(`${API_BASE}/proveedores?${topParams.toString()}`);
      setTopProveedores(topData.data || []);

    } catch (error) {
      console.error('Error cargando datos:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [rangoPeriodo]);

  // Crear la función de cambio de rango como useCallback
  const handleRangoChange = useCallback((nuevoRango) => {
    setRangoPeriodo(nuevoRango);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (number) => {
    return (number || 0).toLocaleString('es-AR');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Cargando estadísticas...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="error">
          ❌ Error: {error}
        </Typography>
      </Box>
    );
  }

  if (!estadisticas) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">
          No hay datos disponibles
        </Typography>
      </Box>
    );
  }

  // Calcular concentración del top 30
  const totalGeneral = estadisticas.total_abonado || 0;
  const totalTop30 = topProveedores.reduce((sum, p) => sum + (parseFloat(p.total_abonado) || 0), 0);
  const porcentajeTop30 = totalGeneral > 0 ? (totalTop30 / totalGeneral * 100) : 0;
  const restante = totalGeneral - totalTop30;
  const porcentajeRestante = 100 - porcentajeTop30;

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        📊 Estadísticas del Sistema
      </Typography>

      {/* Selector temporal con slider */}
      <SelectorTemporalSlider 
        onRangoChange={handleRangoChange} 
        rangoActivo={[0, 100]}
      />

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatNumber(estadisticas.total_proveedores)}
              </Typography>
              <Typography variant="body2">
                Total Proveedores
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssessmentIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatNumber(estadisticas.total_movimientos)}
              </Typography>
              <Typography variant="body2">
                Total Movimientos
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatCurrency(estadisticas.total_facturado).replace('$', '$')}
              </Typography>
              <Typography variant="body2">
                Total Facturado
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AttachMoneyIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatCurrency(estadisticas.total_abonado).replace('$', '$')}
              </Typography>
              <Typography variant="body2">
                Total Abonado
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Análisis de concentración */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrophyIcon sx={{ mr: 1, color: 'gold' }} />
                Análisis de Concentración
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Distribución del poder de compra:
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    🔝 Top 30 Proveedores:
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(totalTop30)} ({porcentajeTop30.toFixed(1)}%)
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    📊 Resto ({formatNumber((estadisticas.total_proveedores || 0) - 30)} proveedores):
                  </Typography>
                  <Typography variant="h6" color="secondary">
                    {formatCurrency(restante)} ({porcentajeRestante.toFixed(1)}%)
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary">
                  <strong>Concentración:</strong> Los 30 principales proveedores representan el {porcentajeTop30.toFixed(1)}% 
                  del total de la facturación municipal.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrophyIcon sx={{ mr: 1, color: 'gold' }} />
                Top 10 Proveedores
              </Typography>
              
              <List dense>
                {topProveedores.slice(0, 10).map((proveedor, index) => {
                  const monto = parseFloat(proveedor.total_abonado) || 0;
                  const porcentaje = totalGeneral > 0 ? (monto / totalGeneral * 100) : 0;
                  
                  return (
                    <ListItem key={proveedor.id} sx={{ px: 0 }}>
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Chip 
                            label={`#${index + 1}`} 
                            size="small" 
                            color={index < 3 ? 'primary' : 'default'}
                            sx={{ mr: 1, minWidth: 35 }}
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              flexGrow: 1, 
                              fontWeight: index < 3 ? 'bold' : 'normal',
                              fontSize: '0.875rem'
                            }}
                          >
                            {proveedor.razon_social.length > 30 
                              ? proveedor.razon_social.substring(0, 30) + '...' 
                              : proveedor.razon_social}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(monto)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {porcentaje.toFixed(2)}%
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Información adicional */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📈 Resumen Ejecutivo
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Proveedor Principal:
                </Typography>
                <Typography variant="body2">
                  {topProveedores[0]?.razon_social || 'N/A'}
                </Typography>
                <Typography variant="body2" color="primary">
                  {formatCurrency(parseFloat(topProveedores[0]?.total_abonado) || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Promedio por Proveedor:
                </Typography>
                <Typography variant="body2" color="primary">
                  {formatCurrency(
                    estadisticas.total_proveedores > 0 
                      ? estadisticas.total_abonado / estadisticas.total_proveedores 
                      : 0
                  )}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Promedio por Movimiento:
                </Typography>
                <Typography variant="body2" color="primary">
                  {formatCurrency(
                    estadisticas.total_movimientos > 0 
                      ? estadisticas.total_abonado / estadisticas.total_movimientos 
                      : 0
                  )}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EstadisticasProveedores;